/**
 * Processing Snapshot - Active processing jobs
 * @module components/starnexus/dashboard/ProcessingSnapshot
 *
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import ProgressBar from '@amzn/awsui-components-console/progress-bar';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';

import { processingService, datasetService } from '../../../services/starnexus';
import type { ProcessingFile, Dataset } from '../../../types/starnexus';

interface ProcessingJob {
  datasetId: string;
  datasetName: string;
  files: ProcessingFile[];
  totalProgress: number;
}

export const ProcessingSnapshot: React.FC = () => {
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [liveAnnouncement, setLiveAnnouncement] = useState<string>('');
  const prevJobsRef = useRef<ProcessingJob[]>([]);

  const fetchJobs = useCallback(async () => {
    try {
      const [activeFiles, allDatasets]: [ProcessingFile[], Dataset[]] = await Promise.all([
        processingService.getActiveFiles(),
        datasetService.listDatasets().then((r: any) => r.items).catch((): Dataset[] => []),
      ]);
      const jobMap = new Map<string, ProcessingJob>();

      activeFiles.forEach((file) => {
        if (!jobMap.has(file.datasetId)) {
          const dataset = allDatasets.find((d: any) => d.datasetId === file.datasetId);
          jobMap.set(file.datasetId, {
            datasetId: file.datasetId,
            datasetName: dataset?.datasetName || file.datasetId,
            files: [],
            totalProgress: 0
          });
        }
        jobMap.get(file.datasetId)!.files.push(file);
      });

      const jobList = Array.from(jobMap.values()).map((job) => {
        const processingFiles = job.files.filter((f) => f.status === 'processing');
        const totalFiles = job.files.length;
        let totalProgress = 0;
        processingFiles.forEach((f) => {
          totalProgress += (f.progress || 0) / totalFiles;
        });
        return { ...job, totalProgress: Math.round(totalProgress) };
      });

      setJobs(jobList);
    } catch {
      // Keep existing state on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();

    // Poll for real progress updates every 5 seconds
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Announce significant progress changes to screen readers
  useEffect(() => {
    if (prevJobsRef.current.length > 0) {
      const announcements: string[] = [];

      jobs.forEach((job) => {
        const prevJob = prevJobsRef.current.find((j) => j.datasetId === job.datasetId);
        if (prevJob) {
          // Announce when a job reaches 100%
          if (job.totalProgress >= 100 && prevJob.totalProgress < 100) {
            announcements.push(`${job.datasetName} processing complete`);
          }
          // Announce significant progress milestones (every 25%)
          const prevMilestone = Math.floor(prevJob.totalProgress / 25);
          const currentMilestone = Math.floor(job.totalProgress / 25);
          if (currentMilestone > prevMilestone && job.totalProgress < 100) {
            announcements.push(`${job.datasetName}: ${job.totalProgress}% complete`);
          }
        }
      });

      if (announcements.length > 0) {
        setLiveAnnouncement(announcements.join('. '));
      }
    }
    prevJobsRef.current = jobs;
  }, [jobs]);

  if (loading) {
    return (
      <Container header={<Header variant="h2">Active Processing</Header>}>
        <Box textAlign="center" padding="l">
          <StatusIndicator type="loading">Loading...</StatusIndicator>
        </Box>
      </Container>
    );
  }

  if (jobs.length === 0) {
    return (
      <Container header={<Header variant="h2">Active Processing</Header>}>
        <Box textAlign="center" color="inherit" padding="l">
          <SpaceBetween size="s">
            <StatusIndicator type="success">All caught up!</StatusIndicator>
            <Box variant="p" color="inherit">
              No files are currently being processed.
            </Box>
          </SpaceBetween>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header variant="h2" counter={`(${jobs.length})`}>
          Active Processing
        </Header>
      }
    >
      {/* Aria-live region for real-time progress updates */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0
        }}
      >
        {liveAnnouncement}
      </div>

      <SpaceBetween size="l">
        {jobs.map((job) => {
          const processingCount = job.files.filter((f) => f.status === 'processing').length;
          const queuedCount = job.files.filter((f) => f.status === 'queued').length;

          return (
            <div key={job.datasetId}>
              <SpaceBetween size="xs">
                <Box>
                  <Link href={`#/datasets/${job.datasetId}`} fontSize="heading-s" ariaLabel={`View ${job.datasetName} details`}>
                    {job.datasetName}
                  </Link>
                </Box>
                <ProgressBar
                  value={job.totalProgress}
                  label={`${processingCount} processing, ${queuedCount} queued`}
                  description={`${job.files.length} total files`}
                  ariaLabel={`${job.datasetName}: ${job.totalProgress}% complete, ${processingCount} processing, ${queuedCount} queued`}
                />
              </SpaceBetween>
            </div>
          );
        })}
      </SpaceBetween>
    </Container>
  );
};

export default ProcessingSnapshot;
